import { Platform } from "react-native";

// The account the app works in: your own (owner null), or one shared with you
// (Teams, audits/team-access-plan-2026-10/PLAN.md §5.5). One owner per screen:
// while you work in someone else's account, every GraphQL request about events
// carries `x-el-owner: <owner>` (App.js), and the server acts in that account
// with the role it gave you (eventlive-cdk/appsync/teams-youthstream/CONTRACT.md).
//
// Module-level, outside React, because App.js's GraphQL headers function runs
// outside React. Screens read it with useWorkspace() (components/teams/useWorkspace.js);
// components/teams/actions.js switches and verifies it. It does nothing until
// App.js switches it on (enableTeamsWorkspace) when the Teams UI is on
// (components/teams/flag.js: the web), so the store apps are unchanged.

export const OWNER_HEADER = "x-el-owner";
// ?as=<owner> on the web: a reload or a shared link opens the same account.
export const AS_PARAM = "as";
const STORAGE_PREFIX = "eventlive.workspace.";

// Root fields that always act on the signed-in user, so their requests never
// carry x-el-owner: your profile and Profile screens, buying (receipts and
// saved cards are yours), tests (they stay in your own account), and your own
// memberships. The server ignores the header for most of these anyway
// (CONTRACT.md POLICY "P"); for broadcasterProfile and buying it would answer
// for the other account or refuse.
export const PERSONAL_FIELDS = new Set([
  "broadcasterProfile",
  "updateBroadcasterProfile",
  "updateEmail",
  "deleteAccount",
  "createTestEvent",
  "resetEvent",
  "createCheckoutSession",
  "creditBroadcaster",
  "sharedWithMe",
  "acceptTeamInvite",
  "leaveTeam",
  "teamInvite",
]);

// Usernames never contain "#", ":", "/", "?", "&" or spaces (the server's key
// rule, eventlive-cdk/teams/src/ids.ts, plus what can't sit in a URL).
const OWNER_RE = /^[^#:\s/?&]{1,200}$/;
export const isOwnerName = (value) =>
  typeof value === "string" && OWNER_RE.test(value);

// ---- the store ---------------------------------------------------------------

const initialState = (enabled) => ({
  enabled,
  // The signed-in username (custom:broadcaster), null when signed out.
  self: null,
  // The account being worked in; null = your own.
  owner: null,
  // "own" | "checking" (owner set, not confirmed yet) | "active"
  status: "own",
  // The signed-in user's remembered workspace has been read.
  restored: !enabled,
  // sharedWithMe rows: [{ owner, owner_name, company_name, logo_path, slug, event_title, role, app }]
  shared: [],
  sharedLoaded: false,
  sharedAt: 0,
  sharedError: null,
  // The owner's profile as the server shows it to members (no email, credits…).
  profile: null,
  // One message to show (e.g. why the app went back to your own account).
  notice: null,
  // The user whose remembered workspace has been checked with the server.
  checkedFor: null,
});

let state = initialState(false);
const listeners = new Set();

export const getWorkspaceState = () => state;

export const subscribeWorkspace = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function setWorkspaceState(patch) {
  const previousOwner = activeOwner(state);
  state = { ...state, ...patch };
  if (activeOwner(state) !== previousOwner) syncWorkspaceUrl();
  listeners.forEach((listener) => listener());
}

// Tests only.
export function resetWorkspaceForTests({ enabled = true } = {}) {
  state = initialState(enabled);
  urlOwner = null;
  ownEvents = { self: null, keys: new Set() };
  listeners.clear();
}

// The owner requests should name, or null (own account, Teams off, signed out).
export const activeOwner = (s = state) =>
  s.enabled && s.self && s.owner && s.owner !== s.self ? s.owner : null;

export const getWorkspaceOwner = () => activeOwner(state);

// ---- headers -----------------------------------------------------------------

// The top-level fields of a GraphQL document (aliases resolved). A small
// scanner: Amplify hands App.js's headers function the printed query.
export function rootFields(query) {
  const src = String(query || "")
    .replace(/"""[\s\S]*?"""/g, '""')
    .replace(/"(?:[^"\\\n]|\\.)*"/g, '""')
    .replace(/#[^\n\r]*/g, " ");
  const isNameStart = (c) => /[_A-Za-z]/.test(c);
  const isNameChar = (c) => /[_0-9A-Za-z]/.test(c);
  const fields = [];
  let depth = 0;
  // Parentheses hold variable definitions and arguments (whose defaults and
  // input objects can contain braces): nothing in them is a field.
  let parens = 0;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (c === "(") parens++;
    else if (c === ")") parens--;
    else if (parens > 0) continue;
    else if (c === "{") depth++;
    else if (c === "}") {
      depth--;
      if (depth === 0) break;
    } else if (depth === 1 && (c === "@" || c === ".")) {
      // A directive or a fragment spread: skip its name.
      let j = i + 1;
      while (j < src.length && (src[j] === "." || /\s/.test(src[j]))) j++;
      while (j < src.length && isNameChar(src[j])) j++;
      i = j - 1;
    } else if (depth === 1 && isNameStart(c)) {
      let j = i + 1;
      while (j < src.length && isNameChar(src[j])) j++;
      let name = src.slice(i, j);
      let k = j;
      while (k < src.length && /[\s,]/.test(src[k])) k++;
      if (src[k] === ":") {
        // alias: field
        k++;
        while (k < src.length && /\s/.test(src[k])) k++;
        let m = k;
        while (m < src.length && isNameChar(src[m])) m++;
        name = src.slice(k, m);
        j = m;
      }
      if (name && name !== "__typename") fields.push(name);
      i = j - 1;
    }
  }
  return fields;
}

export const isSubscriptionDocument = (query) =>
  /^\s*subscription\b/.test(String(query || "").replace(/#[^\n\r]*/g, " "));

export function isPersonalOperation(query) {
  const fields = rootFields(query);
  return (
    fields.length > 0 && fields.every((field) => PERSONAL_FIELDS.has(field))
  );
}

// Your own events met while working in another account: test events stay in
// your own account (createTestEvent ignores the header), so requests about
// them (by slug or event id) go without it.
let ownEvents = { self: null, keys: new Set() };
export function rememberOwnEvent(event, s = state) {
  if (!s.self || !event || event.broadcaster !== s.self) return;
  if (ownEvents.self !== s.self) ownEvents = { self: s.self, keys: new Set() };
  if (event.slug) ownEvents.keys.add(`slug:${event.slug}`);
  if (event.id) ownEvents.keys.add(`id:${event.id}`);
}
const isOwnEventRequest = (variables, s) => {
  if (!variables || ownEvents.self !== s.self) return false;
  return (
    (typeof variables.slug === "string" &&
      ownEvents.keys.has(`slug:${variables.slug}`)) ||
    (typeof variables.event_id === "string" &&
      ownEvents.keys.has(`id:${variables.event_id}`))
  );
};

// App.js: the headers Amplify adds to every query and mutation. `request` is
// { query, variables } (undefined for the realtime socket: subscriptions don't
// go through the owner pipelines).
export function workspaceRequestHeaders(request, s = state) {
  const owner = activeOwner(s);
  if (!owner) return {};
  const query =
    request && typeof request.query === "string" ? request.query : "";
  if (
    !query ||
    isSubscriptionDocument(query) ||
    isPersonalOperation(query) ||
    isOwnEventRequest(request.variables, s)
  ) {
    return {};
  }
  return { [OWNER_HEADER]: owner };
}

// Headers for one request about `owner`'s data (an event's broadcaster),
// overriding the workspace's: your own test events while you work in another
// account (the server treats your own name like no header). Empty outside a
// shared workspace, so requests in your own account stay exactly as they were.
export function ownerHeaders(owner, s = state) {
  if (!activeOwner(s) || !isOwnerName(owner)) return {};
  return { [OWNER_HEADER]: owner };
}

// "Act as me" for one request while working in another account.
export const selfHeaders = (s = state) => ownerHeaders(s.self, s);

// evt.live's routes (video uploads, fundraising, Stripe, the support chat)
// act for the owner this names, once they've checked your role there
// (eventlive-domain lib/donations/server.js authenticateOwner). Empty in your
// own account and for your own events (a test event's slug), so those
// requests stay exactly as they were.
export function evtLiveOwnerHeaders(slug, s = state) {
  const owner = activeOwner(s);
  if (!owner) return {};
  if (typeof slug === "string" && isOwnEventRequest({ slug }, s)) return {};
  return { [OWNER_HEADER]: owner };
}

// ---- remembering it ----------------------------------------------------------

const isWeb = () => Platform.OS === "web" && typeof window !== "undefined";
const storageKey = (self) => `${STORAGE_PREFIX}${self}`;
// The apps remember the choice in AsyncStorage (the web uses localStorage
// directly, to read it before the first request). Loaded when first needed,
// so modules that only send requests don't load it.
const asyncStorage = () => {
  const mod = require("@react-native-async-storage/async-storage");
  return mod?.default || mod;
};

// ?as=<owner> from the address the app was opened with, used once.
let urlOwner = null;
const readUrlOwner = () => {
  if (!isWeb()) return null;
  try {
    const value = new URLSearchParams(window.location?.search || "").get(
      AS_PARAM
    );
    return isOwnerName(value) ? value : null;
  } catch {
    return null;
  }
};

// The signed-in username as the web's sign-in storage has it (Amplify keeps
// the tokens in localStorage under CognitoIdentityServiceProvider.<client>.*),
// read before anything renders. Only a hint: the server checks every request,
// and AuthContext corrects it (or signs out) moments later.
export function readStoredBroadcaster(clientId, storage) {
  try {
    const prefix = `CognitoIdentityServiceProvider.${clientId}`;
    const user = storage?.getItem(`${prefix}.LastAuthUser`);
    const idToken = user && storage?.getItem(`${prefix}.${user}.idToken`);
    const part = idToken ? idToken.split(".")[1] : null;
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(
      decodeURIComponent(
        Array.from(atob(base64))
          .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
          .join("")
      )
    );
    const broadcaster = payload?.["custom:broadcaster"];
    return isOwnerName(broadcaster) ? broadcaster : null;
  } catch {
    return null;
  }
}

// App.js, at start, when the Teams UI flag is on. On the web it also learns
// who's signed in right away, so a reload's first screen already knows the
// account it works in (?as=<owner> or the remembered one).
export function enableTeamsWorkspace({ clientId } = {}) {
  if (state.enabled) return;
  urlOwner = readUrlOwner();
  setWorkspaceState(initialState(true));
  if (isWeb() && clientId) {
    let storage = null;
    try {
      storage = window.localStorage;
    } catch {
      storage = null;
    }
    const self = readStoredBroadcaster(clientId, storage);
    if (self) setWorkspaceSelf(self);
  }
}

function readStoredOwnerSync(self) {
  if (!isWeb()) return null;
  try {
    return window.localStorage?.getItem(storageKey(self)) || null;
  } catch {
    return null;
  }
}

export function persistWorkspaceOwner(self, owner) {
  if (!self) return;
  const key = storageKey(self);
  if (isWeb()) {
    try {
      if (owner) window.localStorage?.setItem(key, owner);
      else window.localStorage?.removeItem(key);
    } catch {
      // Storage refused (private window): the URL still carries ?as=.
    }
    return;
  }
  try {
    const storage = asyncStorage();
    (owner ? storage.setItem(key, owner) : storage.removeItem(key)).catch(
      () => {}
    );
  } catch {
    // No storage on this device: the choice lasts until the app closes.
  }
}

// The signed-in user (AuthContext calls this before it stores the profile, so
// the first requests of a reload already name the remembered account). Another
// user, or signing out, starts over; the remembered choice stays per user.
export function setWorkspaceSelf(self) {
  if (!state.enabled) return;
  const next = isOwnerName(self) ? self : null;
  if (next === state.self) return;
  if (!next) {
    setWorkspaceState({ ...initialState(true), restored: true });
    return;
  }
  const fromUrl = urlOwner;
  urlOwner = null;
  const candidate = [fromUrl, readStoredOwnerSync(next)].find(
    (owner) => isOwnerName(owner) && owner !== next
  );
  setWorkspaceState({
    ...initialState(true),
    self: next,
    owner: candidate || null,
    status: candidate ? "checking" : "own",
    // The web reads its choice right here; the apps read AsyncStorage below.
    restored: isWeb() || !!fromUrl,
  });
  if (!isWeb() && !fromUrl) {
    Promise.resolve()
      .then(() => asyncStorage().getItem(storageKey(next)))
      .catch(() => null)
      .then((stored) => {
        if (state.self !== next || state.restored) return;
        const owner = isOwnerName(stored) && stored !== next ? stored : null;
        setWorkspaceState({
          owner,
          status: owner ? "checking" : "own",
          restored: true,
        });
      });
  }
}

// Keeps ?as=<owner> in the web address (or takes it out in your own account),
// without adding a history entry. React Navigation rewrites the address on
// every navigation, so AppRouting calls this after each state change too.
export function syncWorkspaceUrl(s = state) {
  if (!s.enabled || !isWeb()) return;
  try {
    const url = new URL(window.location.href);
    const want = activeOwner(s);
    const have = url.searchParams.get(AS_PARAM);
    if ((want || null) === (have || null)) return;
    if (want) url.searchParams.set(AS_PARAM, want);
    else url.searchParams.delete(AS_PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch {
    // An address the browser won't let us rewrite: nothing to keep in sync.
  }
}
